import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { setThanks, type Post } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { useRefreshBoards } from '../feed/queries';
import styles from './ThanksButton.module.css';

/** The one lightweight reaction. The count is gentle and never ranks anything. */
export function ThanksButton({ post }: { post: Post }) {
  const toast = useToast();
  const refresh = useRefreshBoards();
  const [optimistic, setOptimistic] = useState<{ thanked: boolean; count: number } | null>(null);
  const thanked = optimistic?.thanked ?? post.viewerThanked;
  const count = optimistic?.count ?? post.thanksCount;

  const toggle = useMutation({
    mutationFn: (next: boolean) => setThanks(post.id, next),
    onMutate: (next) => setOptimistic({ thanked: next, count: count + (next ? 1 : -1) }),
    onSuccess: (serverCount, next) => setOptimistic({ thanked: next, count: Number(serverCount) }),
    onError: (error) => {
      setOptimistic(null);
      toast.show(errorMessage(error));
    },
    onSettled: () => refresh(),
  });

  if (post.isOwn) {
    if (count === 0) return null;
    return (
      <span className={styles.count}>
        <Icon name="heart" size={16} />
        {count === 1 ? '1 neighbor said thanks' : `${count} neighbors said thanks`}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={styles.thanks}
      aria-pressed={thanked}
      onClick={() => toggle.mutate(!thanked)}
      disabled={toggle.isPending}
    >
      <Icon name="heart" size={17} filled={thanked} />
      {count > 0 ? `Thanks ${count}` : 'Thanks'}
    </button>
  );
}
